<script setup>
/**
 * ECharts 的唯一一层壳：建实例、跟着容器 resize、卸载时 dispose、把点击原样 emit 出去。
 *
 * **页面不直接碰 `echarts`。** 它们传一个 option 进来（用 `chart-theme.js` 的 token 配色拼），
 * 点击了哪个类目从 `@select` 出去。散在各页面里各 `echarts.init` 一次，就会各自漏一个 dispose、
 * 各自忘一次 resize。
 */
import * as echarts from 'echarts'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
  /** 完整的 ECharts option（配色由调用方从主题 token 取）。 */
  option: { type: Object, required: true },
  /** 图高，CSS 长度。 */
  height: { type: String, default: '320px' },
})

const emit = defineEmits(['select'])

/** @type {import('vue').Ref<HTMLElement|null>} */
const host = ref(null)
/** @type {import('echarts').ECharts|null} */
let chart = null
/** @type {ResizeObserver|null} */
let observer = null

onMounted(() => {
  if (!host.value) throw new Error('EChart: 容器没挂上')
  chart = echarts.init(host.value)
  chart.setOption(props.option)
  // 点柱子 / 点扇区：把类目名和它的下标给出去，页面自己决定跳哪。
  chart.on('click', (params) => emit('select', { name: params.name, index: params.dataIndex }))
  observer = new ResizeObserver(() => chart?.resize())
  observer.observe(host.value)
})

// notMerge=true：series 数量变了（比如筛掉一个模型）时，旧的那条不会赖在图上。
watch(
  () => props.option,
  (option) => chart?.setOption(option, true),
)

onBeforeUnmount(() => {
  observer?.disconnect()
  chart?.dispose()
  chart = null
})
</script>

<template>
  <div
    ref="host"
    class="echart"
    :style="{ height }"
  />
</template>

<style scoped>
.echart {
  width: 100%;
  min-width: 0;
}
</style>
